import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Customer } from '@api/Customers/Customer';
import * as Yup from 'yup';
import { normalizePhone } from '@api/Customers/Customer';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { showAlertError } from '@utils/Alerts/Error';
import { hideLoader, showLoader } from '@utils/Loader';
import CustomerService from '@api/Customers/CustomerCtrl';
import { NewCustomerData } from '@api/Customers/CustomerServ';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';

interface ModalAddCustomerProps {
    modalAddCustomer: OpenModalHookType;
    customer?: Customer;
    onAddCustomer?: (customer: Customer) => void;
}

const addCustomerSchema = Yup.object({
    uid: Yup.string(),
    firstName: Yup.string().required('Nombres requerido'),
    lastName: Yup.string(),
    // 10 dígitos exactos: `phone` es la IDENTIDAD del cliente (login por OTP y
    // búsqueda del POS). Sin este candado entraron 102 clientes con teléfono
    // inválido — incompletos, o con el nombre y el teléfono intercambiados — que
    // no podían iniciar sesión y cuyos puntos quedaron atrapados (2026-08-19).
    phone: Yup.string()
        .required('Telefono requerido')
        .test('phone-10', 'El teléfono debe tener 10 dígitos', (value) => normalizePhone(value).length === 10),
    email: Yup.string(),
    birthdayDay: Yup.mixed<number | string>().test('birthdayDay', 'Día debe ser entre 1 y 31', (value) => {
        if (!value || value === '') return true;
        const num = Number(value);
        return !isNaN(num) && num >= 1 && num <= 31;
    }),
    birthdayMonth: Yup.mixed<number | string>().test('birthdayMonth', 'Mes debe ser entre 1 y 12', (value) => {
        if (!value || value === '') return true;
        const num = Number(value);
        return !isNaN(num) && num >= 1 && num <= 12;
    }),
    birthdayYear: Yup.mixed<number | string>().test('birthdayYear', 'Año debe ser válido', (value) => {
        if (!value || value === '') return true;
        const num = Number(value);
        const currentYear = new Date().getFullYear();
        return !isNaN(num) && num >= 1900 && num <= currentYear;
    })
});

export const ModalAddCustomer: React.FunctionComponent<ModalAddCustomerProps> = (props: any) => {
    const { modalAddCustomer, customer, onAddCustomer } = props;
    const customerService = useMemo(() => new CustomerService(), []);
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<NewCustomerData>({
        resolver: yupResolver(addCustomerSchema)
    });

    const handleClose = () => {
        reset();
        modalAddCustomer.toggle();
    };
    const onAddCustomerForm: SubmitHandler<NewCustomerData> = async (values) => {
        showLoader();
        try {
            const customerData = {
                ...values,
                email: values.email && values.email.trim() !== '' ? values.email : undefined,
                birthdayDay: values.birthdayDay || undefined,
                birthdayMonth: values.birthdayMonth || undefined,
                birthdayYear: values.birthdayYear || undefined,
                branchOfficeName: branchOfficeSelected?.name,
                // La sucursal que registra: el doc guarda el id y el CRM
                // recibe el nombre como "Sucursal de registro".
                branchOfficeId: branchOfficeSelected?.id
            };
            const customer = await customerService.saveCustomer(customerData);
            onAddCustomer(customer);
            handleClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        if (modalAddCustomer.modalOpen) {
            reset({
                uid: customer?.uid || '',
                firstName: customer?.profile?.firstName || '',
                lastName: customer?.profile?.lastName || '',
                phone: customer?.phone || '',
                email: customer?.email || '',
                birthdayDay: customer?.profile?.birthdayDay || '',
                birthdayMonth: customer?.profile?.birthdayMonth || '',
                birthdayYear: customer?.profile?.birthdayYear || ''
            });
        }
    }, [modalAddCustomer.modalOpen, customer]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalAddCustomer.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-add-customer-title"
            aria-describedby="modal-add-customer-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-add-customer-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                        <Stack direction="row" alignItems="center">
                            <Typography variant="h6" fontWeight={600}>
                                {customer ? 'Editar cliente' : 'Nuevo cliente'}
                            </Typography>
                        </Stack>
                        <Typography variant="body2">
                            {customer ? 'Modifica los datos del cliente' : 'Completa los datos del cliente'}
                        </Typography>
                    </Stack>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ overflow: 'visible' }}>
                <form id="add-customer-form" onSubmit={handleSubmit(onAddCustomerForm)}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Controller
                                name="firstName"
                                control={control}
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Nombres del cliente"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.firstName}
                                        helperText={errors.firstName?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="lastName"
                                control={control}
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        label="Apellidos del cliente"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.lastName}
                                        helperText={errors.lastName?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="phone"
                                control={control}
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Telefono del cliente"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.phone}
                                        helperText={errors.phone?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="email"
                                control={control}
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        label="Correo del cliente"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Fecha de cumpleaños (opcional)
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Controller
                                name="birthdayDay"
                                control={control}
                                defaultValue={undefined}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        label="Día"
                                        type="number"
                                        variant="outlined"
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : Number(e.target.value);
                                            onChange(val);
                                        }}
                                        error={!!errors.birthdayDay}
                                        helperText={errors.birthdayDay?.message}
                                        inputProps={{ min: 1, max: 31 }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Controller
                                name="birthdayMonth"
                                control={control}
                                defaultValue={undefined}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        label="Mes"
                                        type="number"
                                        variant="outlined"
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : Number(e.target.value);
                                            onChange(val);
                                        }}
                                        error={!!errors.birthdayMonth}
                                        helperText={errors.birthdayMonth?.message}
                                        inputProps={{ min: 1, max: 12 }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Controller
                                name="birthdayYear"
                                control={control}
                                defaultValue={undefined}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        label="Año (opcional)"
                                        type="number"
                                        variant="outlined"
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? undefined : Number(e.target.value);
                                            onChange(val);
                                        }}
                                        error={!!errors.birthdayYear}
                                        helperText={errors.birthdayYear?.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button fullWidth color="primary" variant="contained" type="submit" form="add-customer-form">
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
